'use client';

import { useRecoilValue } from 'recoil';
import {
  preallocatedSupplyState,
  selectedProjectBidStatsState,
  selectedProjectBidsState,
  wlSupplyState,
} from '@/lib/state';
import { lamportsToToken } from '@/lib/utils';

import BidProgressBarSkeleton from '@/components/BidProgressBarSkeleton';
import StackedProgress from '@/components/StackedProgress';

export default function BidProgressBar({ decimals, token, showRefundZone = false }) {
  const progress = useRecoilValue(selectedProjectBidsState);
  const stats = useRecoilValue(selectedProjectBidStatsState);
  const wlSupply = useRecoilValue(wlSupplyState);

  const preallocatedSupply = useRecoilValue(preallocatedSupplyState);

  if (!progress) {
    return <BidProgressBarSkeleton />;
  }

  const { total: to, count: c, wl_count: wlCount, num_presales: numPresales } = progress;
  const total = Number(to || 0);
  const count = Number(c || 0);
  const numPreallocated = Number(preallocatedSupply || 0) + Number(numPresales || 0);
  const pctComplete = total === 0 ? 0 : Math.round(((count + numPreallocated) / total) * 100);

  let allocations = [
    {
      title: 'WL Purchases',
      size: wlCount || 0,
      classes: 'bg-pink-400',
    },
    {
      title: 'Public Phase Bids',
      size: count - (wlCount || 0),
      from: (showRefundZone ? wlSupply : wlCount) || 0,
      classes: 'bg-accent',
    },
  ];

  if (showRefundZone) {
    allocations = [
      {
        title: 'WL Refund Zone',
        size: wlSupply || 0,
        classes: 'bg-amber-500',
      },
      ...allocations,
    ];

    // If we're not yet at capacity, shrink WL Refund Zone accordingly
    if (count + numPreallocated < total) {
      const reducedWlSize = Math.max(0, wlSupply - (total - (count + numPreallocated || 0)));
      allocations[0].size = reducedWlSize;
      allocations[1].size = reducedWlSize < 1 ? wlCount : Math.min(wlCount, reducedWlSize);
      allocations[2].from = reducedWlSize;
    }
  }

  let highest, median;
  if (stats && stats?.percentiles?.length === 20) {
    highest = stats?.percentiles?.find((x) => x.percentile === 100).lamports;
    median = stats?.percentiles?.find((x) => x.percentile === 50).lamports;
  }

  return (
    <div className="flex flex-col space-y-2 p-4 font-light">
      <div className="hidden bg-base-300 bg-pink-400" />
      {/* Top row */}
      <div className="flex items-center justify-between">
        <p className="font-medium">Auction Progress</p>
        {total > 0 && (
          <div className="flex space-x-2">
            <div className="font-medium">{Number(pctComplete).toLocaleString()}%</div>
            <div>
              ({Number(count + numPreallocated).toLocaleString()} / {total.toLocaleString()})
            </div>
          </div>
        )}
        {!total && <p>None</p>}
      </div>

      {/* Progress bar */}
      <StackedProgress
        totalSupply={total}
        preallocated={numPreallocated || 0}
        allocations={allocations}
        pctComplete={pctComplete}
        className=""
      />

      {/* Bottom row */}
      {highest && median && (
        <div className="flex items-center justify-between text-gray-300">
          <div className="flex items-start space-x-1">
            <div className="tooltip" data-tip="Live winning bids: 50% above, 50% below this value.">
              Median Bid: <span className="font-semibold">{lamportsToToken(decimals, median)}</span>{' '}
              {token === 'SOL' ? '◎' : token}
            </div>
          </div>
          <div className="flex items-start space-x-1">
            <p>
              Highest Bid:{' '}
              <span className="font-semibold">{lamportsToToken(decimals, highest)}</span>{' '}
              {token === 'SOL' ? '◎' : token}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
